import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, BadgeCheck, MapPin, CalendarCheck } from "lucide-react";
import { SITE, IMAGES, HERO_LINES } from "@/content/site";
import { MaskedLine } from "@/components/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden bg-gradient-to-br from-navy via-deepsea to-ocean">
      <div className="noise-overlay absolute inset-0 opacity-[0.05] pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-aqua/15 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[28rem] h-[28rem] rounded-full bg-lagoon/15 blur-[100px] pointer-events-none" />

      <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold border border-white/15 rounded-full px-4 py-2 bg-white/5 backdrop-blur-sm"
              data-testid="hero-overline"
            >
              <MapPin className="w-3.5 h-3.5" /> Locally Owned &amp; Operated in Pinellas County, FL
            </motion.p>

            <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="hero-headline">
              <MaskedLine delay={0.3}>{HERO_LINES[0]}</MaskedLine>
              <MaskedLine delay={0.45} className="text-transparent bg-clip-text bg-gradient-to-r from-sky2 via-aqua to-sky2">
                {HERO_LINES[1]}
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
              data-testid="hero-subcopy"
            >
              Reliable weekly pool service, professional water chemistry, and clear communication
              after every visit — from a local, CPO certified team that treats your backyard like our own.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-9 flex flex-col sm:flex-row gap-3.5"
            >
              <Link
                to="/contact/"
                data-testid="hero-request-quote-button"
                className="group inline-flex items-center justify-center gap-2.5 bg-aqua text-navy font-bold px-8 py-4 rounded-full text-base transition-all duration-300 hover:bg-sky2 hover:text-navy hover:shadow-xl hover:shadow-cyan-500/30 hover:-translate-y-0.5"
              >
                Request a Quote
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="hero-call-button"
                className="inline-flex items-center justify-center gap-2.5 border-2 border-white/25 text-white font-bold px-8 py-4 rounded-full text-base transition-all duration-300 hover:bg-white hover:text-navy"
              >
                <Phone className="w-5 h-5" />
                {SITE.phone}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
              data-testid="hero-trust-chips"
            >
              {[
                { icon: BadgeCheck, label: "CPO Certified" },
                { icon: CalendarCheck, label: "Consistent Weekly Routes" },
                { icon: MapPin, label: "All of Pinellas County" },
              ].map((c, i) => (
                <span key={c.label} className="inline-flex items-center gap-2 text-sm text-slate-300">
                  <c.icon className={`w-4 h-4 ${i === 1 ? "text-lagoonlight" : "text-aqua"}`} /> {c.label}
                </span>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{ y: imgY }}
              className="relative"
            >
              <div className="clip-arch overflow-hidden shadow-2xl shadow-navy/50 ring-1 ring-white/20">
                <img
                  src={IMAGES.heroSpa}
                  alt="Sparkling residential pool and spa in Pinellas County FL maintained by Point of Pines Pools & Service"
                  className="w-full h-[300px] sm:h-[420px] lg:h-[520px] object-cover"
                  loading="eager"
                  data-testid="hero-image"
                />
              </div>
              <motion.div
                style={{ y: cardY }}
                className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl shadow-navy/30 p-4 sm:p-5 flex items-center gap-3.5"
                data-testid="hero-floating-card"
              >
                <span className="w-11 h-11 rounded-xl bg-mist flex items-center justify-center shrink-0">
                  <BadgeCheck className="w-6 h-6 text-pool" />
                </span>
                <span>
                  <span className="block font-display font-bold text-navy text-sm">CPO Certified Care</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Professional water chemistry, every visit</span>
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
